import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, ChevronLeft, CheckCircle2 } from "lucide-react";

/**
 * Public page where any user can request a password-reset email.
 * For privacy we never reveal whether the email exists — the success state
 * is identical regardless.
 */
export default function ForgotPassword() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error(t("forgot.err.email"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      // Backend always returns ok=true; keep the same UX even on rare network errors.
      setSubmitted(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1B2E7A] mb-4"
          data-testid="forgot-back-link"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> {t("forgot.back")}
        </Link>

        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8">
          {submitted ? (
            <div data-testid="forgot-submitted">
              <div className="h-10 w-10 rounded-full bg-[#1B2E7A]/10 flex items-center justify-center mb-3">
                <CheckCircle2 className="h-5 w-5 text-[#1B2E7A]" strokeWidth={1.6} />
              </div>
              <h1 className="font-heading text-2xl text-[#1A1A1A] mb-2">{t("forgot.sent.title")}</h1>
              <p className="text-sm text-[#374151] leading-relaxed">{t("forgot.sent.body", { email })}</p>
              <p className="text-xs text-[#6B7280] mt-4">{t("forgot.sent.hint")}</p>
              <div className="mt-6 pt-4 border-t border-[#E5E7EB] flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => { setEmail(""); setSubmitted(false); }}
                  className="text-xs text-[#1B2E7A] hover:text-[#13205A] underline"
                  data-testid="forgot-resend"
                >
                  {t("forgot.resend")}
                </button>
                <Link to="/login" className="text-xs text-[#6B7280] hover:text-[#374151]">
                  {t("forgot.go_login")}
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Mail className="h-5 w-5 text-[#1B2E7A]" strokeWidth={1.6} />
                <h1 className="font-heading text-2xl text-[#1A1A1A]">{t("forgot.title")}</h1>
              </div>
              <p className="text-sm text-[#6B7280] mb-6">{t("forgot.subtitle")}</p>
              <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
                <div>
                  <label className="block text-xs text-[#374151] mb-1" htmlFor="forgot-email">
                    {t("forgot.email")}
                  </label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@canalesfranchise.com"
                    autoComplete="email"
                    data-testid="forgot-email"
                    required
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#1B2E7A] hover:bg-[#13205A] text-white"
                  data-testid="forgot-submit"
                >
                  {busy ? t("forgot.sending") : t("forgot.submit")}
                </Button>
              </form>
              <p className="text-[11px] text-[#6B7280] mt-4 leading-snug">{t("forgot.privacy")}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
